import { useTranslation } from "@/hooks";
import { useBookmarksContext } from "@/stores/bookmark";
import { useUserFiles } from "@/stores/user-file";
import type { BookmarkTreeNodeData } from "@/types/bookmark";
import type {
  Bookmark,
  BookmarkSet,
  BookmarkSetBookmark,
} from "@/types/europafacts";
import type { UserFile } from "@/types/workspace";
import {
  Box,
  Flex,
  Text,
  Tree,
  type RenderTreeNodePayload,
} from "@mantine/core";
import {
  IconBookmark,
  IconFile,
  IconFileCode,
  IconFileSpreadsheet,
  IconFileText,
  IconFolder,
  IconFolderHeart,
  IconFolderOpen,
  type TablerIcon,
} from "@tabler/icons-react";
import { Fragment, useMemo, type CSSProperties } from "react";
import "./BookmarkTree.css";

export function BookmarkTree({
  onDatasetSelect,
}: {
  onDatasetSelect: (datasetId: string) => void;
}) {
  const { bookmarks, bookmarkSets } = useBookmarksContext();
  const userFiles = useUserFiles();
  const { currentLanguage, t } = useTranslation();

  const getFileIcon = (mimeType?: string): TablerIcon => {
    if (!mimeType) return IconFile;
    if (mimeType.includes("csv")) return IconFileText;
    if (mimeType.includes("excel") || mimeType.includes("spreadsheet"))
      return IconFileSpreadsheet;
    if (mimeType.includes("json")) return IconFileCode;
    return IconFile;
  };

  const getFileType = (mimeType?: string): string => {
    if (!mimeType) return t("dataset").file;
    if (mimeType.includes("csv")) return t("myData").csv;
    if (mimeType.includes("excel") || mimeType.includes("spreadsheet"))
      return t("myData").excel;
    if (mimeType.includes("geo+json")) return t("myData").geojson;
    if (mimeType.includes("json")) return t("myData").json;
    return t("dataset").file;
  };

  const datasetItems: BookmarkTreeNodeData[] = [
    {
      value: "my-data",
      label: `${t("dataset").myData} (${userFiles.length})`,
      isFolder: true,
      children: userFiles.map((file: UserFile) => ({
        value: file.id,
        label: file.name,
        icon: getFileIcon(file.type),
        fileType: getFileType(file.type),
        isFolder: false,
      })),
    },
    {
      value: "my-bookmarks",
      label: `${t("dataset").favorites} (${bookmarks.length})`,
      icon: IconFolderHeart,
      isFolder: true,
      children: bookmarks.map((bookmark: Bookmark) => ({
        value: bookmark.bookmark_id,
        label: bookmark.db_table.table_name_translation[currentLanguage],
        icon: IconBookmark,
        isFolder: false,
      })),
    },
    ...bookmarkSets?.map((bookmarkSet: BookmarkSet) => ({
      value: bookmarkSet.bookmark_set_id,
      label: `${bookmarkSet.bookmark_set_name} (${
        bookmarkSet.bookmark_set_bookmark_list?.length ?? 0
      })`,
      isFolder: true,
      children:
        bookmarkSet.bookmark_set_bookmark_list?.map(
          (bookmark: BookmarkSetBookmark) => ({
            value: bookmark.bookmark_set_bookmark_id,
            label: bookmark.db_table.table_name_translation[currentLanguage],
            icon: IconBookmark,
            isFolder: false,
          })
        ) ?? [],
    })),
  ];

  const renderNode = ({
    node,
    expanded,
    hasChildren,
    elementProps,
  }: RenderTreeNodePayload) => {
    const extendedNode = node as BookmarkTreeNodeData;
    const FileIcon = extendedNode.icon;
    const isFolder = extendedNode.isFolder ?? hasChildren;

    const iconProps = {
      size: 22,
      stroke: 1,
      style: { flexShrink: 0 },
    };

    const FolderIcon: TablerIcon = expanded
      ? IconFolderOpen
      : FileIcon ?? IconFolder;
    const Icon: TablerIcon = isFolder ? FolderIcon : FileIcon ?? Fragment;

    const groupStyle: CSSProperties = {
      cursor: isFolder ? "default" : "pointer",
      minWidth: 0,
      width: "100%",
      paddingRight: "18px",
    };

    const clickHandler: Record<string, () => void> = !isFolder
      ? { onClick: () => onDatasetSelect(node.value) }
      : {};

    const className: string = useMemo(
      () => `${isFolder ? "folder" : "file"} ${expanded ? "expanded" : ""}`,
      [isFolder, expanded]
    );

    return (
      <Flex
        {...elementProps}
        {...clickHandler}
        style={groupStyle}
        className={className}
        gap={4}
        wrap="nowrap"
      >
        <Icon {...iconProps} />
        <Text
          className="file-name"
          style={{ flex: 1, minWidth: 0 }}
          truncate="end"
          fz={15}
          fw={400}
        >
          {node.label}
        </Text>
      </Flex>
    );
  };

  return (
    <Box
      className="bookmark-tree-container"
      style={{ width: "100%", minWidth: 0 }}
    >
      <Tree data={datasetItems} levelOffset={0} renderNode={renderNode} />
    </Box>
  );
}
